<template>
  <div class="goods">
    <section class="hero" v-if="categoryPic">
      <div
        class="hero-bg"
        :style="{ backgroundImage: `url(${categoryPic})` }"
      ></div>
      <div
        v-if="categoryTitle || categorySubTitle"
        class="hero-text"
      >
        <div v-if="categoryTitle" class="hero-title">
          {{ categoryTitle }}
        </div>
        <div v-if="categorySubTitle" class="hero-subtitle">
          {{ categorySubTitle }}
        </div>
      </div>
    </section>

    <section class="grid container">
      <section class="filterbar" v-if="filters.length">
        <div class="filter-wrap">
          <ul class="filter-items">
            <li class="f-item" v-for="f in filters" :key="f.key">
              <ElDropdown
                trigger="hover"
                popper-class="goods-dropdown-menu"
                @visible-change="(v: boolean) => onDropdownVisibleChange(f.key, v)"
              >
                <span class="f-link">
                  {{ f.label }}
                  <i
                    class="iconfont f-link-icon"
                    :class="isDropdownOpen(f.key) ? 'icon-zhankai' : 'icon-shouqi'"
                  ></i>
                </span>
                <template #dropdown>
                  <ElDropdownMenu>
                    <ElDropdownItem
                      v-for="opt in f.options"
                      :key="opt.value ?? 'all'"
                      @click="onFilter(f.key, opt.value)"
                    >
                      <span class="opt-content">
                        <span>{{ opt.label }}</span>
                        <i
                          v-if="isOptionSelected(f.key, opt.value)"
                          class="iconfont icon-xuanze opt-check"
                        ></i>
                      </span>
                    </ElDropdownItem>
                  </ElDropdownMenu>
                </template>
              </ElDropdown>
            </li>
            <li class="f-item price-range-item">
              <span class="price-label">价格：</span>
              <ElInput
                v-model="priceMin"
                type="text"
                placeholder="最低价"
                class="price-input"
                clearable
                @input="onPriceInput('min', $event)"
                @keyup.enter="onPriceConfirm"
                @clear="onPriceClear"
              />
              <span class="price-separator">-</span>
              <ElInput
                v-model="priceMax"
                type="text"
                placeholder="最高价"
                class="price-input"
                clearable
                @input="onPriceInput('max', $event)"
                @keyup.enter="onPriceConfirm"
                @clear="onPriceClear"
              />
              <ElButton type="primary" size="small" class="price-btn" @click="onPriceConfirm">确定</ElButton>
            </li>
          </ul>
          <div class="selected-tags" v-if="activeTags.length">
            <div class="tag" v-for="t in activeTags" :key="t.key">
              {{ t.label }}: {{ t.valueLabel }}
              <span class="close" @click="onFilter(t.key, null)">×</span>
            </div>
          </div>
        </div>
      </section>
      <div v-if="items.length > 0" class="cards grid-4">
        <div v-for="p in items" :key="p.id" class="card">
          <div class="card-cover">
            <NuxtLink :to="`/goods/${p.prodId ?? p.id}`">
              <ElImage :src="p.img" fit="cover" class="card-image" />
            </NuxtLink>
            <span class="label" v-if="p.hot">热卖</span>
          </div>
          <div class="card-body">
            <div class="name">{{ p.name }}</div>
            <div class="meta">
              <span class="member-price">会员价：￥{{ p.memberPrice || 0 }}</span>
              <span class="original-price">￥{{ p.price }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty-wrap">
        <ElEmpty description="没有符合条件的搜索结果" />
      </div>
      <div v-if="total > 0" class="pagi">
        <ElPagination
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          v-model:current-page="page"
        />
      </div>
    </section>
    <AppFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import AppFooter from "@/components/AppFooter.vue";
import { useRoute } from "#imports";
const categoryPic = ref('');
const categoryTitle = ref('');
const categorySubTitle = ref('');
const currentCate = ref("全部");
const route = useRoute();
const api = useApi();
const filters = ref<{ key: string; label: string; options: { label: string; value: string | number | null }[] }[]>([]);
const selectedFilters = ref<Record<string, string | number>>({});
const openDropdownKey = ref<string | null>(null);

const activeTags = computed(() => {
  const tags = filters.value
    .map(f => {
      const val = selectedFilters.value[f.key];
      if (val === undefined || val === null) return null;
      const opt = f.options.find(o => o.value == val);
      return opt ? { key: f.key, label: f.label, valueLabel: opt.label } : null;
    })
    .filter((x): x is { key: string; label: string; valueLabel: string } => !!x);
  if (appliedPriceMin.value !== null || appliedPriceMax.value !== null) {
    const minLabel = appliedPriceMin.value !== null ? appliedPriceMin.value : '不限';
    const maxLabel = appliedPriceMax.value !== null ? appliedPriceMax.value : '不限';
    tags.push({ key: 'priceRange', label: '价格区间', valueLabel: `${minLabel} - ${maxLabel}` });
  }
  return tags;
});

const isOptionSelected = (key: string, value: string | number | null) => {
  const current = selectedFilters.value[key];
  if (value === null) return current === undefined || current === null;
  return current == value;
};

// 新增方法获取主石类型和颜色筛选数据
const fetchSelectList = async () => {
  try {
    const r = await api.get<any>("/user/querySelectList", {}, { server: false, key: `goods-querySelectList`, retry: 0 });
    const data = (r as any)?.data ?? (r as any)?.raw;
    const selectFilters = [];
    
    // 处理主石类型
    if (Array.isArray(data?.stoneList)) {
      const stoneOptions = [
        { label: '全部', value: null },
        ...data.stoneList.map((item: string) => ({ label: item, value: item }))
      ];
      selectFilters.push({ key: 'stoneList', label: '主石类型', options: stoneOptions });
    }
    
    // 处理主石颜色
    if (Array.isArray(data?.colorList)) {
      const colorOptions = [
        { label: '全部', value: null },
        ...data.colorList.map((item: string) => ({ label: item, value: item }))
      ];
      selectFilters.push({ key: 'colorList', label: '主石颜色', options: colorOptions });
    }
    
    // 添加价格排序选项
    const priceOptions = [
      { label: '综合排序', value: null },
      { label: '价格从低到高', value: 'asc' },
      { label: '价格从高到低', value: 'desc' }
    ];
    selectFilters.push({ key: 'priceSort', label: '价格排序', options: priceOptions });
    
    // 直接设置为新的筛选条件
    filters.value = selectFilters;
  } catch {
    // 出错时清空筛选条件
    filters.value = [];
  }
};

const onDropdownVisibleChange = (key: string, visible: boolean) => {
  if (visible) {
    openDropdownKey.value = key;
  } else if (openDropdownKey.value === key) {
    openDropdownKey.value = null;
  }
};

const isDropdownOpen = (key: string) => openDropdownKey.value === key;

const fetchFilters = async () => {
  try {
    const r = await api.get<any>("/prod/prodNewInfo", {}, { server: false, key: `goods-prodNewInfo`, retry: 0 });
    const data = (r as any)?.data ?? (r as any)?.raw;
    categoryPic.value = data?.imgPc;
    categoryTitle.value = data?.title ?? '';
    categorySubTitle.value = data?.subtitle ?? '';
  } catch {
    // 出错时不影响其他功能
  }
};
onMounted(async () => {
  await fetchFilters();
  await fetchSelectList();
});
const onPriceInput = (type: 'min' | 'max', val: string) => {
  const cleaned = (val || '').replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    const normalized = parts[0] + '.' + parts.slice(1).join('');
    if (type === 'min') {
      priceMin.value = normalized;
    } else {
      priceMax.value = normalized;
    }
  } else {
    if (type === 'min') {
      priceMin.value = cleaned;
    } else {
      priceMax.value = cleaned;
    }
  }
};

const onPriceConfirm = () => {
  const min = priceMin.value !== '' ? Number(priceMin.value) : null;
  const max = priceMax.value !== '' ? Number(priceMax.value) : null;
  appliedPriceMin.value = min !== null && !isNaN(min) ? min : null;
  appliedPriceMax.value = max !== null && !isNaN(max) ? max : null;
  page.value = 1;
  fetchProducts();
};

const onPriceClear = () => {
  onPriceConfirm();
};

const clearPriceRange = () => {
  priceMin.value = '';
  priceMax.value = '';
  appliedPriceMin.value = null;
  appliedPriceMax.value = null;
};

const onFilter = (key: string, val: string | number | null) => {
  if (key === 'priceRange') {
    clearPriceRange();
  } else {
    if (val === null) {
      delete selectedFilters.value[key];
    } else {
      selectedFilters.value[key] = val;
    }
  }
  page.value = 1;
  fetchProducts();
};

const items = ref<any[]>([]);
const total = ref(0);
const page = ref(1);
const pageSize = 16;
const priceMin = ref<string>('');
const priceMax = ref<string>('');
const appliedPriceMin = ref<number | null>(null);
const appliedPriceMax = ref<number | null>(null);
const normalizeProduct = (p: any) => {
  return {
    id: p?.id ?? p?.prodId ?? p?.productId ?? p?.spuId,
    prodId: p?.prodId ?? p?.id ?? p?.productId ?? p?.spuId,
    name: p?.name ?? p?.prodName ?? p?.spuName ?? p?.title,
    price: p?.price ?? p?.actualPrice ?? p?.minPrice ?? p?.originPrice,
    memberPrice: p?.memberPrice ?? p?.vipPrice ?? 0,
    img: p?.img ?? p?.pic ?? p?.imgUrl ?? p?.skuImg ?? p?.cover,
    hot: !!(p?.hot ?? p?.isHot)
  };
};
const fetchProducts = async () => {
  const q = route.query as any;
  const params: Record<string, any> = {
    current: page.value,
    size: pageSize,
  };
  params.newProduct = 1;
  if (q.isAllProdType !== undefined) params.isAllProdType = q.isAllProdType;
  
  // 处理筛选参数
  Object.entries(selectedFilters.value).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (key === 'stoneList') {
        // 主石类型：传stone参数
        params.stone = value;
      } else if (key === 'colorList') {
        // 主石颜色：传color参数
        params.color = value;
      } else if (key === 'priceSort') {
        // 价格排序：传orderBy参数，1为升序，2为降序
        if (value === 'asc') {
          params.orderBy = 1;
        } else if (value === 'desc') {
          params.orderBy = 2;
        }
      }
    }
  });

  // 价格区间参数
  if (appliedPriceMin.value !== null) {
    params.priceMin = appliedPriceMin.value;
  }
  if (appliedPriceMax.value !== null) {
    params.priceMax = appliedPriceMax.value;
  }
  
  try {
    const v = await api.get<any>("/prod/pageProd", params, { server: false, key: `goods-pageProd-${params.current}-${params.categoryId ?? 'all'}`, retry: 0 });
    const list = Array.isArray(v?.data?.records)
      ? v.data.records
      : Array.isArray((v as any)?.records)
      ? (v as any).records
      : Array.isArray(v?.data)
      ? v.data
      : Array.isArray((v as any))
      ? (v as any)
      : [];
    items.value = list.map((x: any) => normalizeProduct(x));
    total.value = Number(v?.data?.total ?? (v as any)?.total ?? list.length) || 0;
  } catch {}
};
await fetchProducts();
watch(() => route.query, () => { page.value = 1; fetchProducts(); });
watch(() => route.query, async () => {
  await fetchFilters();
  await fetchSelectList();
});
watch(page, fetchProducts);
</script>

<style scoped>
.goods {
  font-size: 0.16rem;
}
.hero {
  position: relative;
  height: 5.6rem;
}
.hero-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
}
.hero-text {
  position: absolute;
  left: 0.9rem;
  bottom: 0.94rem;
  color: #ffffff;
}
.hero-title {
  font-size: 0.44rem;
}
.hero-subtitle {
  margin-top: 0.24rem;
  font-size: 0.2rem;
  line-height: 0.32rem;
}
.container {
  width: 100%;
  padding: 0 0.9rem;
}
.filter-wrap {
  padding: 0.2rem 0 0.52rem;
}

.price-range-item {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  font-size: 0.16rem;
  color: #333;
}

.price-label {
  white-space: nowrap;
  color: #333;
}

.price-input {
  width: 1.2rem;
}

.price-separator {
  color: #999;
  padding: 0 0.04rem;
}

.price-btn {
  margin-left: 0.06rem;
}
.bread {
  display: none;
}
.filter-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.36rem;
  align-items: center;
}
:deep(.goods-dropdown-menu .el-dropdown-menu) {
  min-width: 1.2rem;
}
.f-item {
  font-size: 0.16rem;
  cursor: pointer;
}
.f-link {
  color: #333;
  position: relative;
  display: inline-block;
  border: none;
}

.f-link-icon {
  margin-left: 0.06rem;
  font-size: 0.16rem;
  color: #121212;
}
.el-dropdown,
.el-dropdown * {
  outline: none !important;
}
.el-dropdown:focus,
.el-dropdown:focus-visible {
  outline: none !important;
}
.grid {
  margin: 0.4rem auto;
}
.cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.3rem;
}
.card {
  overflow: hidden;
}
.card-cover {
  position: relative;
}
.label {
  position: absolute;
  top: 0.1rem;
  left: 0.1rem;
  background: #000;
  color: #fff;
  font-size: 0.12rem;
  padding: 0.06rem 0.1rem;
  border-radius: 0.04rem;
}
.card-image {
  width: 100%;
  aspect-ratio: 2 / 3;
}
.card-image :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.card-body {
  padding: 0.3rem 0;
  text-align: center;
}
.opt-content {
  display: flex;
  align-items: center;
}
.opt-check {
  margin-left: 0.13rem;
  font-size: 0.16rem;
  color: #121212;
}
.selected-tags {
  margin-top: 0.2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
}
.tag {
  background: #121212;
  color: #fff;
  padding: 0.1rem 0.12rem;
  font-size: 0.16rem;
  display: flex;
  align-items: center;
  gap: 0.1rem;
}
.tag .close {
  cursor: pointer;
  font-size: 0.16rem;
  line-height: 1;
}
.name {
  font-size: 0.14rem;
  margin-bottom: 0.10rem;
  color: #424446;
}
.meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.1rem;
}
.member-price {
  font-size: 0.16rem;
  color: #FF4444;
  font-weight: 600;
}
.original-price {
  font-size: 0.14rem;
  color: #999;
  text-decoration: line-through;
}
.pagi {
  display: flex;
  justify-content: center;
  margin: 0.4rem 0 1rem;
}
.empty-wrap {
  padding: 0.6rem 0 1rem;
}

@media (max-width: 1280px) {
  .container {
    padding: 0 0.48rem;
  }
  .cards {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 960px) {
  .container {
    padding: 0 0.24rem;
  }
  .cards {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 640px) {
  .cards {
    grid-template-columns: 1fr;
  }
}
</style>